import React from 'react';
import styled from 'styled-components';
import { AI_TOOLS } from '@constants';
import { Body5, Body7 } from '@ui';
import { GhostButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { Field } from '@features/query/form/Field';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
import { FormStateContext } from '@/contexts/FormStateContext';
import { FeedInputModal } from './FeedInputModal';
import { COPY, FEED_INPUTS_MAX_ENTRIES, chipSubtitle, liveFeedInputs } from './feedInputs';

// "Score Feed Data": the attachment beside CSV Data on the Data Analyst Agent form. Same chip box
// and the same two buttons as CsvDocAttachment; the chips and the picker are its own, because a
// feed is not a document — SelectedDocument opens the document viewer when pressed and needs a
// filename, neither of which a feed has.
//
// Renders nothing without the feed_view grant. Not attempt-and-catch: the feed API answers a 403
// to an account without the grant, and the one route this attachment may also reach (the score
// spec catalog) runs handle400Statuses, so a user who cannot use this must never see it.

const SelectedWrapper = styled.div`
  max-height: 200px;
  min-height: 42px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 8px;
`;
const ChipsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`;
const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;

  padding-top: 16px;
`;

// SelectedDocument's look — a thin primary border, red when the server rejected it — without its
// behaviour.
const Chip = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  max-width: 480px;
  padding: 3px 6px 3px 8px;
  border-radius: 2px;
  border: 1px solid
    ${({ theme, $invalid }) => ($invalid ? theme.themeColors.error : theme.themeColors.primaryCtaButton)};

  ${({ theme, $invalid }) =>
    $invalid
      ? `
    * { color: ${theme.themeColors.error} !important; }
  `
      : ''}
`;
const ChipText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  > * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const ChipRemove = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
`;

const FeedInputChip = ({ item, invalid, invalidReason, onRemove }) => (
  <Chip $invalid={invalid} title={invalid && invalidReason ? invalidReason : undefined}>
    <ChipText>
      <Body5>{item.id}</Body5>
      <Body7 isLowContrast>{invalid && invalidReason ? invalidReason : chipSubtitle(item)}</Body7>
    </ChipText>
    <ChipRemove aria-label={COPY.removeChip(item.id)} onClick={() => onRemove(item)}>
      <span aria-hidden="true">×</span>
    </ChipRemove>
  </Chip>
);

const FeedInputsPicker = ({ canUseSpecs, canReadSpecs }) => {
  const { state, setFormStateValue, addFeedInputs } = React.useContext(FormStateContext);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  // The chips are the feeds whose rows are still in hand. An item released on kickoff stays in form
  // state for the session card and is neither drawn here nor offered to the picker's duplicate check.
  const allItems = state.selectedFeedInputs || [];
  const items = React.useMemo(() => liveFeedInputs(allItems), [allItems]);
  const invalidIds = state.invalidFeedIds || [];
  const errorById = state.feedErrorById || {};
  const invalidIdSet = React.useMemo(() => new Set(invalidIds), [invalidIds]);

  // Through the reducer, not a closure read of the list: the modal's attach loop calls this once
  // per feed from a closure that outlives the render it started in, and anything that read the
  // list here would append every feed onto the same pre-click copy.
  const handleAttach = React.useCallback((item) => addFeedInputs(item), [addFeedInputs]);

  // One removal clears the item, its invalid mark and its reason together, as the CSV picker does.
  // The invalid list and the error map are reconciled against the chips that REMAIN, not against
  // the ids removed: an id the server spelled differently from its chip, or one that outlived its
  // chip, matched nothing and could never be cleared, and Submit stayed disabled until a reload.
  const removeByIds = React.useCallback(
    (idSet) => {
      const remaining = allItems.filter((item) => !idSet.has(item.id));
      setFormStateValue('selectedFeedInputs', remaining);
      const remainingIds = new Set(remaining.map((item) => item.id));
      const nextInvalid = invalidIds.filter((id) => remainingIds.has(id));
      if (nextInvalid.length !== invalidIds.length) setFormStateValue('invalidFeedIds', nextInvalid);
      const nextErrors = {};
      let mutated = false;
      Object.keys(errorById).forEach((id) => {
        if (remainingIds.has(id)) nextErrors[id] = errorById[id];
        else mutated = true;
      });
      if (mutated) setFormStateValue('feedErrorById', nextErrors);
    },
    [allItems, invalidIds, errorById, setFormStateValue]
  );

  const handleRemove = React.useCallback((item) => removeByIds(new Set([item.id])), [removeByIds]);

  const handleRemoveAll = React.useCallback(() => {
    if (!items.length) return;
    removeByIds(new Set(items.map((item) => item.id)));
  }, [items, removeByIds]);

  const atCapacity = items.length >= FEED_INPUTS_MAX_ENTRIES;

  return (
    <div>
      {isModalOpen ? (
        <FeedInputModal
          onDismiss={() => setIsModalOpen(false)}
          items={items}
          onAttach={handleAttach}
          canUseSpecs={canUseSpecs}
          canReadSpecs={canReadSpecs}
        />
      ) : null}
      {items.length > 0 && (
        <SelectedWrapper>
          <ChipsRow>
            {items.map((item) => {
              const invalid = invalidIdSet.has(item.id);
              return (
                <FeedInputChip
                  key={item.id}
                  item={item}
                  invalid={invalid}
                  invalidReason={invalid ? errorById[item.id] : undefined}
                  onRemove={handleRemove}
                />
              );
            })}
          </ChipsRow>
        </SelectedWrapper>
      )}
      <ActionsWrapper>
        {/* The shared button's `disabled` is styling only, so the guard is in the handler. */}
        <SkeletonButton onClick={() => !atCapacity && setIsModalOpen(true)} disabled={atCapacity}>
          {COPY.selectButton}
        </SkeletonButton>
        {items.length > 0 && <TransparentFlatButton onClick={handleRemoveAll}>{COPY.removeAll}</TransparentFlatButton>}
        {atCapacity && <Body7 isLowContrast>{COPY.maxEntriesHint}</Body7>}
      </ActionsWrapper>
    </div>
  );
};

export const FeedDocAttachment = () => {
  const tools = useCurrentUserToolsAvailable();
  if (!tools || !tools.includes(AI_TOOLS.FEED_VIEW)) return null;

  return (
    <Field
      label={COPY.fieldLabel}
      component={
        <FeedInputsPicker
          canUseSpecs={tools.includes(AI_TOOLS.SCORE_BACKFILL)}
          canReadSpecs={tools.includes(AI_TOOLS.SCORE_SPEC_ADMIN)}
        />
      }
    />
  );
};
